import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import { useTheme, alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { AvatarUpload, DataView } from "/imports/ui/components/shared";
import { useCheckInPhase } from "/imports/hooks/useCheckInOpen";
import { checkInPolicyText, formatDate } from "../dashboardHelpers";
import CheckInHint from "./CheckInHint";
import PhoneConfirmDialog from "./PhoneConfirmDialog";

function ProfileField({ label, value, action }) {
  return (
    <Grid item xs={12} sm={4}>
      <Typography variant="caption" color="textSecondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
        <Typography variant="body2" fontWeight={500}>
          {value}
        </Typography>
        {action}
      </Box>
    </Grid>
  );
}

/** The dashboard already wraps every tab in a card, so on mobile these sections
    render flat — nesting a second card inside the first reads as clutter and
    breaks with the other tabs, which list their rows straight onto the page. */
function Section({ title, flat, children }) {
  const body = (
    <>
      <Typography variant="h6" gutterBottom sx={{ color: "primary.main", fontWeight: 700 }}>
        {title}
      </Typography>
      {children}
    </>
  );

  if (flat) return <Box>{body}</Box>;

  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>{body}</CardContent>
    </Card>
  );
}

/** Its own component because the check-in window is a hook, and a hook can't
    live inside the .map() that renders these rows. */
function UpcomingBookingRow({ booking, onCheckIn }) {
  const navigate = useNavigate();
  const { slug } = useParams();
  const checkInPhase = useCheckInPhase(booking.checkInWindow);
  const canCheckIn = checkInPhase === "open";

  // CheckInHint renders only in the "before" phase, so anything else would leave an
  // empty second line on a phone.
  const hasRowExtras = Boolean(booking.checkIn) || checkInPhase === "before" || (canCheckIn && onCheckIn);

  return (
    <Box
      sx={{
        py: 1,
        borderBottom: 1,
        borderColor: "divider",
        "&:last-child": { borderBottom: 0 },
        display: "flex",
        // On a phone the check-in hint and the meta line fight over one line, and the
        // meta line loses: it wraps mid-phrase, splitting the trainer from their name.
        // Dropping the extras underneath gives the text the full width.
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "flex-start", sm: "center" },
        gap: { xs: 0.75, sm: 1.5 }
      }}
    >
      <Box flex={1} minWidth={0} width="100%">
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Typography variant="subtitle2">{booking.serviceName}</Typography>
          {booking.game && (
            <Typography
              variant="caption"
              onClick={() => navigate(`/${slug}/bookings/${booking._id}`)}
              sx={{
                cursor: "pointer",
                fontWeight: 700,
                color: booking.game.isHost && booking.game.status === "PENDING_AUTH" ? "warning.main" : "primary.main",
                "&:hover": { textDecoration: "underline" }
              }}
            >
              {booking.game.isHost && booking.game.status === "PENDING_AUTH"
                ? "Finish card hold →"
                : `View game${booking.game.seatsTotal ? ` (${booking.game.seatsTaken ?? 0}/${booking.game.seatsTotal})` : ""} →`}
            </Typography>
          )}
        </Box>
        <Typography variant="body2" color="textSecondary">
          {formatDate(booking.date)} at {booking.time}
          {booking.trainer && ` with ${booking.trainer}`}
          {booking.location && ` - ${booking.location}`}
        </Typography>
        {booking.pendingPurchase && (
          <Typography variant="caption" sx={{ color: "warning.main", fontWeight: 600 }}>
            Reserved — books when your package payment completes
          </Typography>
        )}
      </Box>
      {hasRowExtras && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
            flexShrink: 0,
            justifyContent: { xs: "flex-start", sm: "flex-end" }
          }}
        >
          {booking.checkIn && (
            <Box display="flex" alignItems="center" gap={0.5} sx={{ color: "success.main", flexShrink: 0 }}>
              <HowToRegIcon sx={{ fontSize: "1rem" }} />
              <Typography variant="caption" fontWeight={600}>
                Checked in{booking.checkIn.label ? ` ${booking.checkIn.label}` : ""}
              </Typography>
            </Box>
          )}
          <CheckInHint phase={checkInPhase} opensAt={booking.checkInWindow?.opensAt} />
          {canCheckIn && onCheckIn && (
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={() => onCheckIn(booking)}
              sx={{ height: "1.75rem", px: 1.5, flexShrink: 0, textTransform: "none", color: "common.white", boxShadow: "none" }}
            >
              Check in
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}

function OverviewTab({
  firstName,
  lastName,
  email,
  phone,
  phoneEditable,
  imgUrl,
  onAvatarUpdated,
  onPhoneUpdated,
  dashboard,
  loading,
  error,
  onCheckIn
}) {
  const theme = useTheme();
  const flat = useMediaQuery(theme.breakpoints.down("md"));
  const { slug } = useParams();
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);

  const levels = Object.entries(dashboard?.levels || {});
  const initials = `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();

  // Light tint of the club's primary color — a soft branded panel on desktop.
  // On mobile the dashboard already wraps the tab in a card, so a second panel
  // reads as a card-in-a-card; there we go flat and let the colored headline carry it.
  const bandSx = flat
    ? {}
    : {
        p: 3,
        borderRadius: 4,
        backgroundColor: alpha(theme.palette.primary.main, 0.06),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`
      };

  const profile = (
    <>
      <Grid container spacing={2}>
        <ProfileField label="Name" value={`${firstName} ${lastName}`.trim() || "—"} />
        <ProfileField label="Email" value={email || "—"} />
        {/* The club can't reach an unusable number, and only the person it
            belongs to can say what it should be — so the prompt sits on the
            number itself rather than behind a settings screen. It disappears for
            good once a valid one is saved. */}
        <ProfileField
          label="Phone"
          value={phone || "—"}
          action={
            phoneEditable && (
              <Button
                size="small"
                color="warning"
                onClick={() => setPhoneDialogOpen(true)}
                sx={{ minWidth: 0, px: 0.75, py: 0, textTransform: "none", fontWeight: 700, lineHeight: 1.6 }}
              >
                {phone ? "Confirm" : "Add"}
              </Button>
            )
          }
        />
      </Grid>
      {levels.length > 0 && (
        <Box mt={2} display="flex" gap={1} flexWrap="wrap">
          {levels.map(([sport, level]) => (
            <Chip key={sport} size="small" label={`${sport}: Level ${level}`} color="primary" variant="outlined" />
          ))}
        </Box>
      )}
    </>
  );

  // Only stated to someone it applies to: at least one listed booking has to be
  // one the server would accept a check-in for (it sends a window, whatever the
  // clock says, or the booking is already checked in). A club with check-in off
  // sends no policy and no windows, so nothing about it appears anywhere.
  const hasCheckinableBooking = dashboard?.upcomingBookings?.some((b) => b.checkInWindow || b.checkIn);
  const checkInNote = hasCheckinableBooking ? checkInPolicyText(dashboard?.checkInPolicy) : null;

  const upcomingBookings =
    dashboard?.upcomingBookings?.length > 0 ? (
      <>
        {checkInNote && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              mt: 0.5,
              mb: 2,
              px: 1.5,
              py: 1.25,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "2rem",
                height: "2rem",
                borderRadius: "9999px",
                flexShrink: 0,
                color: "primary.main",
                backgroundColor: alpha(theme.palette.primary.main, 0.12)
              }}
            >
              <HowToRegIcon sx={{ fontSize: "1.125rem" }} />
            </Box>
            <Box minWidth={0}>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "text.primary", lineHeight: 1.3 }}>Self check-in</Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", lineHeight: 1.45 }}>{checkInNote}</Typography>
            </Box>
          </Box>
        )}
        {dashboard.upcomingBookings.map((booking) => (
          <UpcomingBookingRow key={booking._id} booking={booking} onCheckIn={onCheckIn} />
        ))}
      </>
    ) : (
      <Typography variant="body2" color="textSecondary">
        No upcoming bookings
      </Typography>
    );

  const activePlans =
    dashboard?.packages?.length > 0 ? (
      <Box>
        {dashboard.packages.slice(0, 3).map((pkg) => {
          const isMembership = pkg.kind === "membership";
          return (
            <Box key={pkg._id} sx={{ mb: 1 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle2">{pkg.name}</Typography>
                <Chip
                  size="small"
                  label={isMembership ? "Membership" : "Package"}
                  variant="outlined"
                  sx={{ height: "1.125rem", fontSize: "0.65rem", fontWeight: 600, color: "primary.main", borderColor: "primary.main" }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
                {isMembership
                  ? `${pkg.hasMemberAccess ? "Member pricing access" : "Active"}${pkg.noExpiry ? " — No expiry" : pkg.expiryDate ? ` — Expires ${formatDate(pkg.expiryDate)}` : ""}`
                  : `Remaining: ${pkg.sessionsRemaining} sessions${pkg.noExpiry ? " — No expiry" : pkg.expiryDate ? ` — Expires ${formatDate(pkg.expiryDate)}` : ""}`}
              </Typography>
              {/* Same plan bought twice renders identical rows — the
                  purchase date is what tells them apart. */}
              {pkg.purchasedAt && (
                <Typography variant="caption" color="textSecondary">
                  Purchased {formatDate(pkg.purchasedAt)}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    ) : (
      <Typography variant="body2" color="textSecondary">
        No active packages
      </Typography>
    );

  return (
    <>
      <Box
        sx={{
          ...bandSx,
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "center", md: "flex-start" },
          gap: { xs: 1.5, md: 3 }
        }}
      >
        <AvatarUpload imgUrl={imgUrl} initials={initials} size={flat ? 4.5 : 5} onUpdated={onAvatarUpdated} />
        <Box sx={{ flex: 1, minWidth: 0, width: 1 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: "primary.main", textAlign: { xs: "center", md: "left" } }}>
            Welcome back, {firstName || "there"}!
          </Typography>
          {profile}
        </Box>
      </Box>

      {flat && <Divider sx={{ mb: 3 }} />}

      <DataView loading={loading} error={error} data={dashboard} loadingMessage="Loading dashboard...">
        {flat ? (
          <Stack spacing={3} divider={<Divider />}>
            <Section flat title="Upcoming Bookings">
              {upcomingBookings}
            </Section>
            <Section flat title="Active Packages">
              {activePlans}
            </Section>
          </Stack>
        ) : (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Section title="Upcoming Bookings">{upcomingBookings}</Section>
            </Grid>
            <Grid item xs={12} md={6}>
              <Section title="Active Packages">{activePlans}</Section>
            </Grid>
          </Grid>
        )}
      </DataView>

      <PhoneConfirmDialog
        open={phoneDialogOpen}
        slug={slug}
        storedPhone={phone}
        onClose={() => setPhoneDialogOpen(false)}
        onSaved={(client) => {
          setPhoneDialogOpen(false);
          if (onPhoneUpdated) onPhoneUpdated(client);
        }}
      />
    </>
  );
}

export default OverviewTab;
